import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Megaphone, Plus, Pin, Trash2, Pencil, Users, GraduationCap, Heart, Sparkles, CalendarDays, AlertTriangle } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, EmptyState } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";

const ROLE_ICON = { director: Sparkles, teacher: GraduationCap, parent: Heart, dancer: Sparkles };

export default function Announcements() {
  const { user } = useAuth();
  const isStaff = user?.role === "director" || user?.role === "teacher";
  const isDirector = user?.role === "director";
  const [items, setItems] = useState([]);
  const [classes, setClasses] = useState([]);

  const [teachers, setTeachers] = useState([]);
  const load = () => {
    api.get("/announcements").then((r) => setItems(r.data));
    api.get("/classes").then((r) => setClasses(r.data)).catch(() => {});
    if (isDirector) api.get("/people/teachers").then((r) => setTeachers(r.data)).catch(() => {});
  };
  useEffect(load, []);
  const classMap = Object.fromEntries(classes.map((c) => [c.id, c.name]));

  const empty = { title: "", body: "", audience: "all", target_role: "parent", class_id: "", teacher_ids: [], pinned: false, urgent: false, publish_at: "", expires_at: "" };
  const [dlg, setDlg] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);

  const openNew = () => { setForm({ ...empty, class_id: classes[0]?.id || "" }); setEditId(null); setDlg(true); };
  const openEdit = (a) => { setForm({ ...empty, ...a }); setEditId(a.id); setDlg(true); };
  const save = async () => {
    if (!form.title.trim()) { toast.error("Add a title"); return; }
    try {
      if (editId) await api.put(`/announcements/${editId}`, form);
      else await api.post("/announcements", form);
      toast.success("Announcement posted"); setDlg(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const remove = async (a) => {
    try { await api.delete(`/announcements/${a.id}`); toast.success("Removed"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const audienceLabel = (a) => a.audience === "all" ? "Everyone"
    : a.audience === "staff" ? "All staff"
    : a.audience === "selected" ? `${(a.teacher_ids || []).length} teacher(s)`
    : a.audience === "role" ? `${a.target_role}s` : `Class: ${classMap[a.class_id] || "—"}`;

  return (
    <div className="stagger-in">
      <PageHeader title="Announcements" subtitle="Studio news, reminders and class updates." testid="announcements-page"
        action={isStaff && <Button data-testid="add-announcement-button" onClick={openNew} className="rounded-full bg-teal hover:bg-teal-dark"><Plus className="w-4 h-4 mr-2" />New Post</Button>} />

      {items.length === 0 ? <EmptyState icon={Megaphone} title="No announcements yet" hint={isStaff ? "Post your first update." : "Check back soon!"} testid="announcements-empty" /> :
        <div className="space-y-4">
          {items.map((a) => {
            const RI = ROLE_ICON[a.author_role] || Megaphone;
            const canEdit = isDirector || (user?.role === "teacher" && a.author_id === user?.profile_id);
            return (
              <div key={a.id} data-testid={`announcement-${a.id}`} className={`bg-white rounded-2xl border p-6 ${a.urgent ? "border-red-300 shadow-sm" : a.pinned ? "border-teal shadow-sm" : "border-gray-100"}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-2 flex-wrap">
                    {a.urgent && <Badge className="bg-red-600 text-white rounded-full text-[10px] flex items-center gap-1"><AlertTriangle className="w-3 h-3" />Urgent</Badge>}
                    {a.pinned && <Badge className="bg-teal text-white rounded-full text-[10px] flex items-center gap-1"><Pin className="w-3 h-3" />Pinned</Badge>}
                    <Badge variant="outline" className="rounded-full text-[10px]">{audienceLabel(a)}</Badge>
                  </div>
                  {canEdit && <div className="flex gap-1.5 shrink-0">
                    <Button size="sm" variant="outline" className="rounded-full h-8 w-8 p-0" data-testid={`edit-announcement-${a.id}`} onClick={() => openEdit(a)}><Pencil className="w-3.5 h-3.5" /></Button>
                    <Button size="sm" variant="outline" className="rounded-full h-8 w-8 p-0 text-red-600 border-red-200 hover:bg-red-50" data-testid={`delete-announcement-${a.id}`} onClick={() => remove(a)}><Trash2 className="w-3.5 h-3.5" /></Button>
                  </div>}
                </div>
                <h3 className="font-head font-bold text-lg mt-3">{a.title}</h3>
                {a.body && <p className="text-sm text-muted-foreground mt-1.5 whitespace-pre-wrap">{a.body}</p>}
                <p className="text-xs text-muted-foreground mt-4 flex items-center gap-1.5"><RI className="w-3.5 h-3.5 text-teal" />{a.author_name}{a.created_at ? ` · ${new Date(a.created_at).toLocaleDateString()}` : ""}</p>
              </div>
            );
          })}
        </div>}

      <Dialog open={dlg} onOpenChange={setDlg}>
        <DialogContent className="bg-white max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-head flex items-center gap-2"><Megaphone className="w-5 h-5 text-teal" />{editId ? "Edit" : "New"} Announcement</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label>Title</Label><Input data-testid="announcement-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Message</Label><Textarea data-testid="announcement-body" value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} rows={4} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Audience</Label>
                <select data-testid="announcement-audience" className="w-full border rounded-md h-10 px-3" value={form.audience} onChange={(e) => setForm({ ...form, audience: e.target.value })}>
                  <option value="all">Everyone</option>
                  {isDirector && <option value="staff">All staff</option>}
                  {isDirector && <option value="selected">Selected teachers</option>}
                  <option value="role">Specific role</option>
                  <option value="class">Specific class</option>
                </select>
              </div>
              {form.audience === "role" && <div className="space-y-1.5"><Label>Role</Label>
                <select data-testid="announcement-role" className="w-full border rounded-md h-10 px-3" value={form.target_role} onChange={(e) => setForm({ ...form, target_role: e.target.value })}>
                  <option value="teacher">Teachers</option><option value="parent">Parents</option><option value="dancer">Dancers</option>
                </select></div>}
              {form.audience === "class" && <div className="space-y-1.5"><Label>Class</Label>
                <select data-testid="announcement-class" className="w-full border rounded-md h-10 px-3" value={form.class_id} onChange={(e) => setForm({ ...form, class_id: e.target.value })}>
                  {classes.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select></div>}
            </div>
            {form.audience === "selected" && <div className="space-y-1.5"><Label>Teachers</Label>
              <div className="grid grid-cols-2 gap-1.5 max-h-32 overflow-y-auto border rounded-md p-2">
                {teachers.map((t) => (
                  <label key={t.id} className="flex items-center gap-2 text-sm">
                    <input type="checkbox" data-testid={`announcement-teacher-${t.id}`} checked={(form.teacher_ids || []).includes(t.id)}
                      onChange={() => setForm((f) => ({ ...f, teacher_ids: (f.teacher_ids || []).includes(t.id) ? f.teacher_ids.filter((x) => x !== t.id) : [...(f.teacher_ids || []), t.id] }))} />
                    {t.preferred_name || `${t.first_name} ${t.last_name}`}
                  </label>
                ))}
              </div></div>}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Publish on (optional)</Label><Input data-testid="announcement-publish" type="date" value={form.publish_at} onChange={(e) => setForm({ ...form, publish_at: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Expires on (optional)</Label><Input data-testid="announcement-expires" type="date" value={form.expires_at} onChange={(e) => setForm({ ...form, expires_at: e.target.value })} /></div>
            </div>
            <div className="flex items-center gap-6">
              <label className="flex items-center gap-2 text-sm"><Switch data-testid="announcement-pinned" checked={form.pinned} onCheckedChange={(v) => setForm({ ...form, pinned: v })} />Pin to top</label>
              <label className="flex items-center gap-2 text-sm"><Switch data-testid="announcement-urgent" checked={form.urgent} onCheckedChange={(v) => setForm({ ...form, urgent: v })} />Mark urgent</label>
            </div>
          </div>
          <DialogFooter><Button data-testid="save-announcement-button" onClick={save} className="rounded-full bg-teal hover:bg-teal-dark">{editId ? "Save" : "Post"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
